import type { CampoPuntaje } from '@pasos/schemas';
import { claseDeAlto } from '../../components/ui/Barra.js';
import { ICONO, Minus, Plus } from '../../components/ui/iconos.js';
import { PACIENTE } from '../../copy/index.js';

type TermometroProps = {
  campo: CampoPuntaje;
  valor: number | undefined;
  onElegir: (valor: number) => void;
  /** Id del encabezado de la pregunta: es el nombre accesible del grupo. */
  etiquetadoPor: string;
};

const PASO_GRUESO = 10;

/**
 * Termómetro de estado de salud (EQ-5D-5L, 0–100).
 *
 * **No es un slider.** Un `input[type=range]` con el pulgar de un adulto mayor
 * recién operado es un blanco de pocos píxeles que se arrastra sin ver el valor;
 * el skill de escalas lo marca como anti-patrón en móvil, y shadcn/ui todavía no
 * está instalado en el proyecto. Lo que hay en su lugar son cuatro botones de
 * 56px, el número grande a la vista y una barra que sólo informa: cualquiera de
 * las dos manos y cualquier lector de pantalla llegan al mismo valor.
 *
 * Los saltos son de a 10 y de a 1: llegar a 73 son ocho toques, no setenta y tres.
 *
 * **Los botones llevan `aria-label` y no es opcional.** El signo salía de un
 * glifo tipográfico y ahora es un ícono de la librería (ADR-016), que va
 * `aria-hidden`: sin el `aria-label`, los cuatro botones se anunciarían como
 * "10", "10", "1" y "1" y no habría forma de saber cuál sube y cuál baja.
 *
 * E3.8.2, contra la referencia P-06: **se ve como termómetro**, una barra
 * vertical con los extremos del instrumento, y el valor en serif de 88 px. Los
 * botones pasan a 64 px: los de a 10 en navy pleno, los de a 1 en navy suave.
 * Las anclas son las del instrumento y vienen del campo, no de acá.
 */

const BOTON =
  'flex min-h-16 items-center justify-center gap-1 rounded-lg text-display-xs font-bold ' +
  'focus-visible:ring-3 focus-visible:ring-ring focus-visible:ring-offset-2';
const FUERTE = `${BOTON} bg-brand text-primary-foreground`;
const SUAVE = `${BOTON} bg-brand-soft text-link`;
export function Termometro({ campo, valor, onElegir, etiquetadoPor }: TermometroProps) {
  const actual = valor ?? Math.round((campo.minimo + campo.maximo) / 2);
  const proporcion = ((actual - campo.minimo) / Math.max(campo.maximo - campo.minimo, 1)) * 100;

  function mover(delta: number) {
    onElegir(Math.min(campo.maximo, Math.max(campo.minimo, actual + delta)));
  }

  return (
    <fieldset
      aria-labelledby={etiquetadoPor}
      className="m-0 grid grid-cols-[6rem_1fr] gap-6 border-0 p-0"
    >
      {/* Decorativa: el valor accesible es el número de al lado, que se anuncia
          solo al cambiar. Una barra con `role="meter"` lo diría dos veces. El
          alto sale de `claseDeAlto`, en pasos de 5%, por la misma razón que el
          ancho de `Barra`: nada inline. */}
      <div
        aria-hidden="true"
        className="relative h-82.5 overflow-hidden rounded-full bg-track outline-2 -outline-offset-2 outline-border"
      >
        <span className="absolute inset-x-0 top-3 z-1 text-center font-mono text-base text-body">
          {campo.maximo}
        </span>
        <span
          className={`absolute inset-x-0 bottom-0 rounded-full bg-linear-180 from-accent to-brand ${claseDeAlto(proporcion)}`}
        />
        <span
          className={`absolute inset-x-0 bottom-3 z-1 text-center font-mono text-base ${
            proporcion >= 10 ? 'text-primary-foreground' : 'text-body'
          }`}
        >
          {campo.minimo}
        </span>
      </div>

      <div className="flex flex-col justify-between gap-4">
        <p className="m-0 text-base text-subtle">{campo.etiquetaMaximo}</p>

        <p aria-live="polite" className="m-0 text-center font-serif text-display-3xl text-link">
          {actual}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button
            aria-label={PACIENTE.formulario.subirDiez(PASO_GRUESO)}
            className={FUERTE}
            onClick={() => mover(PASO_GRUESO)}
            type="button"
          >
            <Plus aria-hidden="true" className={ICONO.normal} />
            {PASO_GRUESO}
          </button>
          <button
            aria-label={PACIENTE.formulario.subirUno}
            className={SUAVE}
            onClick={() => mover(1)}
            type="button"
          >
            <Plus aria-hidden="true" className={ICONO.normal} />1
          </button>
          <button
            aria-label={PACIENTE.formulario.bajarDiez(PASO_GRUESO)}
            className={FUERTE}
            onClick={() => mover(-PASO_GRUESO)}
            type="button"
          >
            <Minus aria-hidden="true" className={ICONO.normal} />
            {PASO_GRUESO}
          </button>
          <button
            aria-label={PACIENTE.formulario.bajarUno}
            className={SUAVE}
            onClick={() => mover(-1)}
            type="button"
          >
            <Minus aria-hidden="true" className={ICONO.normal} />1
          </button>
        </div>

        <p className="m-0 text-base text-subtle">{campo.etiquetaMinimo}</p>
      </div>
    </fieldset>
  );
}
